import { useRef, type ComponentProps, type FC } from 'react';
import { NavLink, matchPath, resolvePath, useLocation, useResolvedPath } from 'react-router';
import styled from 'styled-components';
import { transparentize } from 'polished';
import { type Page } from '../TopBarUI';

export const MobileNavLinks: FC<{
  className?: string;
  pages: Page[];
  handleHideMenu: () => void;
  isManagerView?: boolean;
}> = ({ className, pages, handleHideMenu, isManagerView = false }) => {
  const resolvedPath = useResolvedPath('', { relative: 'route' });
  const location = useLocation();

  const pagesExt = pages.map((page) => {
    const path = resolvePath(page.path, resolvedPath.pathname);
    const m = matchPath(path.pathname, location.pathname);
    return { ...page, active: !!m };
  });

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <LinksContainer className={className} ref={containerRef}>
      {pagesExt.map(({ name, path, active }) => (
        <NLinkWrapper
          handleHideMenu={handleHideMenu}
          key={path}
          to={path}
          isActive={active}
          $isManagerView={isManagerView}
          style={{ cursor: active ? 'default' : 'pointer' }}
        >
          {name}
        </NLinkWrapper>
      ))}
    </LinksContainer>
  );
};

const NLink = styled(NavLink)<{ $isManagerView?: boolean }>`
  position: relative;
  text-decoration: none;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.normal};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 500;
  height: 100%;
  margin: 0;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid transparent;
  transition:
    background-color 180ms ease,
    border-color 180ms ease,
    color 180ms ease;

  &.active {
    color: ${({ theme, $isManagerView }) => ($isManagerView ? theme.colors.managerPrimary : theme.colors.primary)};
    border-color: ${({ theme, $isManagerView }) =>
      transparentize(0.8, $isManagerView ? theme.colors.managerPrimary : theme.colors.primary)};
    background: ${({ theme, $isManagerView }) =>
      transparentize(0.9, $isManagerView ? theme.colors.managerPrimary : theme.colors.primary)};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.topBarSubtleBackground};
    color: ${({ theme }) => theme.colors.heading};
  }
`;

const NLinkWrapper: FC<ComponentProps<typeof NLink> & { isActive: boolean; handleHideMenu: () => void }> = ({
  children,
  isActive,
  handleHideMenu,
  ...props
}) => {
  return (
    <NLink onClick={handleHideMenu} {...props}>
      {children}
    </NLink>
  );
};

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
